import React, { useRef } from 'react';
import { CONSOLE_HEADER_SECTION_ELEMENT_ID } from 'src/commons/constants';
import { useIsMobile } from '@amzn/storm-ui';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';
import { BreadcrumbTruncationProvider } from './components/BreadcrumbIA2/BreadcrumbTruncationContext';
import { usePopoverResizeObserver } from 'src/commons/utils/hooks';
import {
  useLayoutResizeObserver,
  usePageHeaderLayout,
} from 'src/commons/hooks';
import { ImageProps } from 'src/commons/utils/types';
import { ConnectedConsoleLevelBannerWithAces } from './components/Banners/Common/BannerWithAces';
import { ConnectedPageHeader } from './components/PageHeader/PageHeader';
import SkipNavCard from './components/SkipNavCard/SkipNavCard';
import {
  GlobalStyle,
  HeaderContainer,
  MarketplaceSwitcherWrapper,
  MenuShiftWrapper,
  RegionSwitcherWrapper,
} from './styles';
import { ConsoleHeaderContent, ToastBannerWrapper } from './UtilityComponents';
import MarketplaceSwitcher from './components/MarketplaceSwitcher/MarketplaceSwitcher';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { RegionSwitcherProvider } from './components/RegionSwitcher/RegionSwitcherProvider';
import { AccountDropdownProvider } from 'src/features/header/components/AccountDropdown/AccountDropdownProvider';
import RegionSwitcher from 'src/features/header/components/RegionSwitcher/RegionSwitcher';
import { resolveSwitcherVisibility } from './switcherVisibility';

export const DesktopHeader: React.VFC<{
  shouldRenderMenu: boolean;
  pageLayoutType: PAGE_LAYOUT_TYPE;
  logo: ImageProps;
  internalLogo?: ImageProps;
}> = ({ shouldRenderMenu, pageLayoutType, logo, internalLogo }) => {
  const chromeConfig = useChromeConfigContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const { isInterstitialPage, isFromRodeo } = internalChromeConfig;
  const isMobile = useIsMobile();
  const consoleHeaderSectionRef = useRef<HTMLDivElement>(null);
  const pageHeaderWrapperRef = useRef<HTMLDivElement>(null);

  const marketplaceSwitcher = chromeConfig?.marketplaceSwitcher;
  const { hasMarketplaceSwitcher, hasRegionSwitcher } =
    resolveSwitcherVisibility({
      marketplaceEnabled:
        marketplaceSwitcher === undefined ||
        !!marketplaceSwitcher.useMarketplaceSwitcher,
      marketplaceExplicitlyEnabled:
        !!marketplaceSwitcher?.useMarketplaceSwitcher,
      regionSwitcherEnabled: !!chromeConfig?.regionSwitcherEnabled,
      pageLayoutType,
    });

  usePageHeaderLayout(pageHeaderWrapperRef);

  useLayoutResizeObserver({
    consoleHeaderSectionRef,
  });

  usePopoverResizeObserver({ popoverResizeRef: consoleHeaderSectionRef });

  return (
    <>
      <GlobalStyle
        shouldShiftAppWrapper={shouldRenderMenu}
        isFromRodeo={isFromRodeo}
        isInterstitialPage={isInterstitialPage}
        isMobile={isMobile}
      />
      <SkipNavCard />
      <HeaderContainer
        id={CONSOLE_HEADER_SECTION_ELEMENT_ID}
        ref={consoleHeaderSectionRef}
      >
        <ConnectedConsoleLevelBannerWithAces />
        <BreadcrumbTruncationProvider>
          <ConsoleHeaderContent
            logo={logo}
            internalLogo={internalLogo}
            pageLayoutType={pageLayoutType}
            isMobile={false}
          />
        </BreadcrumbTruncationProvider>
      </HeaderContainer>
      <MenuShiftWrapper
        ref={pageHeaderWrapperRef}
        shouldShiftAppWrapper={shouldRenderMenu}
      >
        {hasMarketplaceSwitcher && (
          <ErrorBoundary name="MarketplaceSwitcher">
            <MarketplaceSwitcherWrapper data-ccx-e2e-id="aac-marketplace-switcher-wrapper">
              <MarketplaceSwitcher
                pathOverride={marketplaceSwitcher?.marketplacePath}
              />
            </MarketplaceSwitcherWrapper>
          </ErrorBoundary>
        )}
        {hasRegionSwitcher && (
          <ErrorBoundary name="RegionSwitcher">
            <AccountDropdownProvider>
              <RegionSwitcherProvider>
                <RegionSwitcherWrapper>
                  <RegionSwitcher />
                </RegionSwitcherWrapper>
              </RegionSwitcherProvider>
            </AccountDropdownProvider>
          </ErrorBoundary>
        )}
        <ConnectedPageHeader />
      </MenuShiftWrapper>
      <ToastBannerWrapper isMobile={false} />
    </>
  );
};
